import { useEffect, useState } from "react";
import { Building2, ChevronDown, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { setAuthToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Accent per workgroup so the active company is unmistakable at a glance.
export const WG_COLORS = {
  elite: { bar: "bg-[#1a3fd1]", chip: "bg-[#1a3fd1] text-white" },
  steeltooth: { bar: "bg-[#c2410c]", chip: "bg-[#c2410c] text-white" },
  jasper: { bar: "bg-[#6d28d9]", chip: "bg-[#6d28d9] text-white" },
};

export const useWorkgroup = () => {
  const [wg, setWg] = useState(null);
  useEffect(() => {
    api.get("/workgroups/current").then((r) => setWg(r.data)).catch(() => {});
  }, []);
  return wg;
};

// Slim bar pinned above the dashboard for superadmins: names the workgroup
// whose data (and admin settings) every screen below is bound to, and lets
// them hop to another one. Switching opens a superadmin session in the
// target workgroup and reloads so nothing from the old one lingers.
const WorkgroupBar = () => {
  const { user } = useAuth();
  const wg = useWorkgroup();
  const [switching, setSwitching] = useState(null);

  if (user?.role !== "superadmin" || user?.impersonating || !wg) return null;
  const colors = WG_COLORS[wg.slug] || WG_COLORS.elite;

  const switchTo = async (slug) => {
    if (slug === wg.slug || switching) return;
    setSwitching(slug);
    try {
      const res = await api.post("/workgroups/switch", { slug });
      setAuthToken(res.data.session_token);
      // browser-side "recent" lists are workgroup data too — drop them so
      // nothing from the previous company shows up in the new one
      ["em_recent_impersonations_v1", "em_recent_projects", "takeoff-active-project"].forEach((k) => {
        try { localStorage.removeItem(k); } catch { /* ignore */ }
      });
      try {
        const secure = window.location.protocol === "https:" ? "; Secure" : "";
        document.cookie = `wg=${slug}; Path=/; Max-Age=${365 * 24 * 3600}; SameSite=Lax${secure}`;
      } catch { /* server already set it */ }
      toast.success(`Switched to ${res.data.name}`);
      window.location.href = "/dashboard";
    } catch (e) {
      setSwitching(null);
      toast.error(e?.response?.data?.detail || "Could not switch workgroup");
    }
  };

  return (
    <div
      className={`${colors.bar} flex shrink-0 items-center justify-between gap-3 px-3 py-1 text-white sm:px-6`}
      data-testid="workgroup-bar"
    >
      <div className="flex min-w-0 items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em]">
        <Building2 className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate" data-testid="workgroup-bar-name">
          {wg.name} workgroup
        </span>
        <span className="hidden text-white/60 sm:inline">
          · {wg.via === "domain" ? "by domain" : wg.via === "override" ? "switched" : "default"}
        </span>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="workgroup-switch-btn"
            disabled={!!switching}
            className="inline-flex items-center gap-1 rounded-md border border-white/40 bg-white/10 px-2 py-0.5 text-[11px] font-semibold hover:bg-white/20 disabled:opacity-60"
          >
            {switching ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            Switch <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid="workgroup-switch-menu">
          <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Workgroups · separate databases
          </DropdownMenuLabel>
          {wg.workgroups.map((w) => (
            <DropdownMenuItem
              key={w.slug}
              onSelect={() => switchTo(w.slug)}
              data-testid={`workgroup-option-${w.slug}`}
              className="cursor-pointer justify-between"
            >
              <span className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${WG_COLORS[w.slug]?.bar || ""}`} />
                {w.name}
              </span>
              {w.slug === wg.slug && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default WorkgroupBar;
